import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Heart,
  AlertTriangle,
  Stethoscope,
  Activity,
  FileSearch,
  Search,
  TrendingUp,
  Syringe,
  Ambulance,
  Info,
  Ban,
  CheckCircle2,
  Droplets,
  Shield
} from 'lucide-react';

export default function TamponnadePage() {
  const [checkedItems, setCheckedItems] = useState({});

  const handleCheck = (section, item) => {
    setCheckedItems(prev => ({
      ...prev,
      [`${section}-${item}`]: !prev[`${section}-${item}`]
    }));
  };

  const sectionsData = [
    {
      id: 'generalites',
      number: '01',
      title: 'Généralités',
      icon: Info,
      color: 'text-cyan-400',
      content: {
        definition: "La tamponnade est une **extrême urgence médicale** qui évolue toujours vers le décès du patient en l'absence de traitement. Elle se définit comme un épanchement péricardique responsable d'une **compression aiguë des cavités cardiaques**.",
        risque: "**ACR (Arrêt Cardio-Respiratoire)** par désamorçage de la pompe cardiaque"
      }
    },
    {
      id: 'diagnostic',
      number: '02',
      title: 'Diagnostic',
      icon: Search,
      color: 'text-amber-400',
      subsections: [
        {
          title: 'Antécédents',
          items: [
            { 
              label: 'Traitement anticoagulant',
              detail: '(+++)',
              critical: true
            },
            { 
              label: 'Péricardite',
              detail: '(++)'
            },
            { 
              label: 'Néoplasie'
            },
            { 
              label: 'Infection, Tuberculose'
            },
            { 
              label: 'Maladies de système'
            },
            { 
              label: 'Traumatisme thoracique récent'
            },
            { 
              label: 'Chirurgie cardiaque ou pose de stimulateur'
            }
          ]
        },
        {
          title: 'Clinique',
          items: [
            { 
              label: 'Triade de Beck (quasi pathognomonique)',
              detail: 'Turgescence jugulaire + hypoTA + assourdissement des BDC',
              critical: true
            },
            { 
              label: 'Signes d\'insuffisance cardiaque droite',
              detail: 'Turgescence jugulaire, hépatomégalie sensible avec RHJ',
              critical: true
            },
            { 
              label: 'Signes d\'insuffisance cardiaque gauche',
              detail: 'Tachycardie, dyspnée d\'effort puis de repos, hypotension'
            },
            { 
              label: 'Choc cardiogénique (à l\'extrême)',
              critical: true
            },
            { 
              label: 'Douleur ou oppression thoracique'
            },
            { 
              label: 'Bruit de roue ou frottement péricardique',
              detail: 'Présent dans 30% des cas'
            },
            { 
              label: 'Pouls paradoxal',
              detail: 'Présent dans 80% des cas - difficile à rechercher'
            },
            { 
              label: 'Bradycardie paradoxale',
              detail: 'Annonce l\'imminence de l\'arrêt cardiaque',
              critical: true
            }
          ]
        },
        {
          title: 'Signes ECG',
          items: [
            { 
              label: 'Tachycardie sinusale',
              detail: 'Quasi constante'
            },
            { 
              label: 'Alternance électrique',
              detail: 'Variation amplitude/morphologie du QRS - très évocateur',
              critical: true
            },
            { 
              label: 'Microvoltage',
              detail: '40% des cas - très évocateur',
              critical: true
            },
            { 
              label: 'Sus-décalage ST et/ou inversion onde T',
              detail: 'Moins spécifiques'
            }
          ]
        },
        {
          title: 'Signes Échocardiographiques (Diagnostic de certitude)',
          items: [
            { 
              label: 'Épanchement péricardique abondant',
              detail: 'Systolo-diastolique, > 1 cm',
              critical: true
            },
            { 
              label: 'Compression diastolique des cavités droites',
              critical: true
            },
            { 
              label: 'Septum paradoxal',
              critical: true
            },
            { 
              label: 'Cœur dansant dans le sac péricardique',
              detail: 'Pathognomonique',
              critical: true
            }
          ]
        }
      ]
    },
    {
      id: 'etiologies',
      number: '03',
      title: 'Étiologies',
      icon: FileSearch,
      color: 'text-purple-400',
      content: {
        medicales: [
          'Affection néoplasique (cause dominante)',
          'Infections',
          'Maladies de système'
        ],
        mecaniques: [
          'Traumatique : Plaie du cœur, contusion myocardique, iatrogénie',
          'Médicale : Rupture en paroi libre dans l\'IDM, dissection aortique'
        ]
      }
    },
    {
      id: 'gravite',
      number: '04',
      title: 'Critères de Gravité',
      icon: AlertTriangle,
      color: 'text-red-400',
      items: [
        { 
          label: 'Patient en ACR à l\'arrivée du SMUR',
          detail: 'Dissociation électromécanique',
          severity: 'critical'
        },
        { 
          label: 'Signes de choc',
          severity: 'high'
        },
        { 
          label: 'Signes de bas débit cérébral',
          severity: 'high'
        },
        { 
          label: 'Bradycardie paradoxale',
          severity: 'critical'
        },
        { 
          label: 'Installation rapide de la tamponnade',
          detail: 'Épanchement d\'origine mécanique (+++)',
          severity: 'critical'
        }
      ]
    },
    {
      id: 'differentiels',
      number: '05',
      title: 'Diagnostics Différentiels',
      icon: Search,
      color: 'text-blue-400',
      content: {
        note: "L'échographie embarquée permet d'éliminer ces diagnostics",
        list: [
          'Embolie pulmonaire',
          'Infarctus du VD (Ventricule Droit)',
          'Épanchement pleural compressif',
          'Insuffisance cardiaque globale terminale'
        ]
      }
    },
    {
      id: 'cat',
      number: '06',
      title: 'Conduite à Tenir',
      icon: Syringe,
      color: 'text-cyan-400',
      subsections: [
        {
          title: 'Objectifs',
          objective: 'Lever la compression myocardique et restaurer la perfusion tissulaire'
        },
        {
          title: 'Dans tous les cas',
          items: [
            { 
              label: 'Repos strict du patient',
              detail: '(+++)',
              critical: true
            },
            { 
              label: 'Position demi-assise'
            },
            { 
              label: 'Monitoring FC et TA continu',
              critical: true
            },
            { 
              label: 'O2 MHC (Masque Haute Concentration)'
            },
            { 
              label: 'Pose de 2 VVP de bon calibre',
              critical: true
            },
            { 
              label: 'Remplissage vasculaire systématique',
              detail: 'Ex: Voluven® 500 ml en 10 min',
              critical: true
            },
            { 
              label: 'Inotropes',
              detail: 'Dobutamine + Noradrénaline ou Adrénaline',
              critical: true
            }
          ]
        },
        {
          title: 'Si pas d\'amélioration ou signes de mort imminente',
          items: [
            { 
              label: 'Ponction péricardique de sauvetage',
              detail: 'Au mieux écho-guidée',
              critical: true
            }
          ]
        }
      ]
    },
    {
      id: 'transport',
      number: '07',
      title: 'Transport',
      icon: Ambulance,
      color: 'text-amber-400',
      content: {
        prioritaire: 'Médicalisé directement dans un bloc de chirurgie cardiaque',
        alternatif: 'À défaut, dans la salle de déchocage d\'une structure disposant d\'un chirurgien thoracique'
      }
    },
    {
      id: 'savoir',
      number: '08',
      title: 'À Savoir',
      icon: Info,
      color: 'text-blue-400',
      items: [
        { 
          label: 'Conserver le liquide de ponction',
          detail: 'Pour études ultérieures (biochimie, cytologie...)'
        },
        { 
          label: 'Ventilation contrôlée : en dernier recours',
          detail: 'Risque d\'aggravation de la compression myocardique',
          critical: true
        },
        { 
          label: 'Ne doit pas comporter de PEEP',
          detail: 'Aggrave la compression',
          critical: true
        }
      ]
    },
    {
      id: 'eviter',
      number: '09',
      title: 'À Éviter',
      icon: Ban,
      color: 'text-red-400',
      items: [
        { 
          label: 'Vasodilatateurs',
          detail: 'Contre-indiqués',
          severity: 'critical'
        },
        { 
          label: 'Diurétiques',
          detail: 'Contre-indiqués',
          severity: 'critical'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="tamponnade-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Heart className="w-10 h-10 text-red-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-red-400 to-red-300 bg-clip-text text-transparent">
              Tamponnade Cardiaque
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm md:text-base max-w-3xl mx-auto">
            Protocole complet de prise en charge d'une extrême urgence cardiologique
          </p>
        </div>

        {/* Alerte Urgence */}
        <Alert className="bg-red-500/10 border-red-500/30 border-2">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <AlertDescription>
            <p className="font-bold text-red-300 text-lg mb-2">⚠️ EXTRÊME URGENCE MÉDICALE</p>
            <p className="text-[hsl(var(--text))]">
              Évolution TOUJOURS mortelle en l'absence de traitement. Risque d'ACR par désamorçage de la pompe cardiaque.
            </p>
          </AlertDescription>
        </Alert>

        {/* Accordéons */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Activity className="w-5 h-5" />
              Guide Complet de Prise en Charge
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="space-y-4">
              {sectionsData.map((section) => {
                const SectionIcon = section.icon;
                
                return (
                  <AccordionItem 
                    key={section.id} 
                    value={section.id}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                    data-testid={`section-${section.id}`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))] transition-colors">
                      <div className="flex items-center gap-3">
                        <Badge className="bg-cyan-500 text-white font-bold">{section.number}</Badge>
                        <SectionIcon className={`w-5 h-5 ${section.color}`} />
                        <span className="font-semibold text-[hsl(var(--text))]">{section.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="mt-3 space-y-4">
                        {/* Généralités */}
                        {section.id === 'generalites' && (
                          <>
                            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                              <p className="text-sm text-[hsl(var(--text))] mb-2" dangerouslySetInnerHTML={{ __html: section.content.definition.replace(/\*\*(.*?)\*\*/g, '<strong class="text-red-300">$1</strong>') }} />
                            </div>
                            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                              <p className="text-xs font-semibold text-amber-300 mb-1">Risque Principal :</p>
                              <p className="text-sm text-[hsl(var(--text))]" dangerouslySetInnerHTML={{ __html: section.content.risque.replace(/\*\*(.*?)\*\*/g, '<strong class="text-amber-300">$1</strong>') }} />
                            </div>
                          </>
                        )}

                        {/* Diagnostic (avec sous-sections) */}
                        {section.id === 'diagnostic' && section.subsections && (
                          <div className="space-y-4">
                            {section.subsections.map((subsec, idx) => (
                              <div key={idx} className="border border-[hsl(var(--border))] rounded-lg p-4">
                                <h4 className="font-semibold text-cyan-300 mb-3">{subsec.title}</h4>
                                <div className="space-y-2">
                                  {subsec.items.map((item, itemIdx) => (
                                    <div 
                                      key={itemIdx}
                                      className={`flex items-start gap-3 p-3 rounded-lg ${
                                        item.critical 
                                          ? 'bg-amber-500/10 border border-amber-500/30' 
                                          : 'bg-[hsl(var(--bg-elev))]'
                                      }`}
                                    >
                                      <Checkbox
                                        id={`${section.id}-${subsec.title}-${itemIdx}`}
                                        checked={checkedItems[`${section.id}-${subsec.title}-${itemIdx}`] || false}
                                        onCheckedChange={() => handleCheck(section.id, `${subsec.title}-${itemIdx}`)}
                                        className="mt-1"
                                      />
                                      <div className="flex-1">
                                        <label 
                                          htmlFor={`${section.id}-${subsec.title}-${itemIdx}`}
                                          className="font-medium text-[hsl(var(--text))] cursor-pointer block"
                                        >
                                          {item.label}
                                        </label>
                                        {item.detail && (
                                          <p className="text-sm text-[hsl(var(--text-dim))] mt-1">{item.detail}</p>
                                        )}
                                        {item.critical && (
                                          <Badge variant="outline" className="mt-2 border-amber-500/30 text-amber-300 text-xs">
                                            Critique
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Étiologies */}
                        {section.id === 'etiologies' && section.content && (
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                              <h4 className="font-semibold text-purple-300 mb-3">Origine Médicale (dominante)</h4>
                              <ul className="space-y-2">
                                {section.content.medicales.map((item, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-[hsl(var(--text-dim))]">
                                    <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-purple-500" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                              <h4 className="font-semibold text-red-300 mb-3">Origine Mécanique</h4>
                              <ul className="space-y-2">
                                {section.content.mecaniques.map((item, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-[hsl(var(--text-dim))]">
                                    <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}

                        {/* Critères de gravité */}
                        {section.id === 'gravite' && section.items && (
                          <div className="space-y-3">
                            {section.items.map((item, idx) => (
                              <div 
                                key={idx}
                                className={`p-4 rounded-lg border ${
                                  item.severity === 'critical' 
                                    ? 'bg-red-500/10 border-red-500/30' 
                                    : 'bg-amber-500/10 border-amber-500/30'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <AlertTriangle className={`w-5 h-5 mt-0.5 ${item.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`} />
                                  <div className="flex-1">
                                    <p className="font-semibold text-[hsl(var(--text))]">{item.label}</p>
                                    {item.detail && (
                                      <p className="text-sm text-[hsl(var(--text-dim))] mt-1">{item.detail}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Diagnostics différentiels */}
                        {section.id === 'differentiels' && section.content && (
                          <>
                            <Alert className="bg-blue-500/10 border-blue-500/30">
                              <Info className="w-4 h-4 text-blue-400" />
                              <AlertDescription className="text-[hsl(var(--text))]">
                                {section.content.note}
                              </AlertDescription>
                            </Alert>
                            <ul className="space-y-2">
                              {section.content.list.map((item, idx) => (
                                <li key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--bg-elev))]">
                                  <CheckCircle2 className="w-4 h-4 text-blue-400" />
                                  <span className="text-sm text-[hsl(var(--text))]">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}

                        {/* Conduite à tenir */}
                        {section.id === 'cat' && section.subsections && (
                          <div className="space-y-4">
                            {section.subsections.map((subsec, idx) => (
                              <div key={idx} className="border border-[hsl(var(--border))] rounded-lg p-4">
                                <h4 className="font-semibold text-cyan-300 mb-3">{subsec.title}</h4>
                                
                                {subsec.objective && (
                                  <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 mb-3">
                                    <p className="text-sm font-semibold text-cyan-300">{subsec.objective}</p>
                                  </div>
                                )}
                                
                                {subsec.items && (
                                  <div className="space-y-2">
                                    {subsec.items.map((item, itemIdx) => (
                                      <div 
                                        key={itemIdx}
                                        className={`flex items-start gap-3 p-3 rounded-lg ${
                                          item.critical 
                                            ? 'bg-amber-500/10 border border-amber-500/30' 
                                            : 'bg-[hsl(var(--bg-elev))]'
                                        }`}
                                      >
                                        <Checkbox
                                          id={`${section.id}-${idx}-${itemIdx}`}
                                          checked={checkedItems[`${section.id}-${idx}-${itemIdx}`] || false}
                                          onCheckedChange={() => handleCheck(section.id, `${idx}-${itemIdx}`)}
                                          className="mt-1"
                                        />
                                        <div className="flex-1">
                                          <label 
                                            htmlFor={`${section.id}-${idx}-${itemIdx}`}
                                            className="font-medium text-[hsl(var(--text))] cursor-pointer block"
                                          >
                                            {item.label}
                                          </label>
                                          {item.detail && (
                                            <p className="text-sm text-[hsl(var(--text-dim))] mt-1">{item.detail}</p>
                                          )}
                                          {item.critical && (
                                            <Badge variant="outline" className="mt-2 border-amber-500/30 text-amber-300 text-xs">
                                              Critique
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Transport */}
                        {section.id === 'transport' && section.content && (
                          <div className="space-y-3">
                            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                              <p className="text-xs font-semibold text-amber-300 mb-2">Prioritaire :</p>
                              <p className="text-sm text-[hsl(var(--text))]">{section.content.prioritaire}</p>
                            </div>
                            <div className="p-4 rounded-lg bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))]">
                              <p className="text-xs font-semibold text-[hsl(var(--text-dim))] mb-2">Alternatif :</p>
                              <p className="text-sm text-[hsl(var(--text-dim))]">{section.content.alternatif}</p>
                            </div>
                          </div>
                        )}

                        {/* À savoir et À éviter */}
                        {(section.id === 'savoir' || section.id === 'eviter') && section.items && (
                          <div className="space-y-3">
                            {section.items.map((item, idx) => (
                              <div 
                                key={idx}
                                className={`p-4 rounded-lg border ${
                                  section.id === 'eviter' 
                                    ? 'bg-red-500/10 border-red-500/30' 
                                    : item.critical 
                                      ? 'bg-amber-500/10 border-amber-500/30'
                                      : 'bg-blue-500/10 border-blue-500/30'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  {section.id === 'eviter' ? (
                                    <Ban className="w-5 h-5 text-red-400 mt-0.5" />
                                  ) : (
                                    <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                                  )}
                                  <div className="flex-1">
                                    <p className="font-semibold text-[hsl(var(--text))]">{item.label}</p>
                                    {item.detail && (
                                      <p className="text-sm text-[hsl(var(--text-dim))] mt-1">{item.detail}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
